import { useEffect, useMemo, useRef, useState } from "react";
import { attachmentUrl } from "../api";
import { sourceLabel } from "../format";
import { useApproveNote, useAttachments, useBlockSender, useDeleteForever, useNote, useUpdateNote } from "../queries";
import { ConfirmButton } from "./ConfirmButton";
import { CategoryInput } from "./CategoryInput";
import { inlineAttachmentNames, renderMarkdown, toggleTask } from "../markdown";
import { baseName, downloadUrl, inlineMedia, isInlineImage, isVideo, mediaUrl } from "../note-media";
import { exportMarkdown, exportPdf, exportText } from "../export";
import { EmailFrame } from "./EmailFrame";
import { Menu } from "./Menu";
import { ShareDialog } from "./ShareDialog";
import { NoteEditor } from "./NoteEditor";

export function NoteDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const note = useNote(id);
  const update = useUpdateNote();
  const approve = useApproveNote();
  const block = useBlockSender();
  const deleteForever = useDeleteForever();
  const attachments = useAttachments(id, Boolean(note.data));
  const allImages = attachments.data?.filter((a) => isInlineImage(a.mime)) ?? [];
  // Web clips from the extension: the page snapshot goes first, above the (often long) text.
  const snapshots = note.data?.source === "extension" ? allImages.filter((a) => /^(Snapshot|Full page)\b/.test(a.filename ?? "")) : [];
  // Photos and videos the Markdown shows in place (X Articles) aren't repeated below it.
  const inlineNames = useMemo(() => inlineAttachmentNames(note.data?.bodyText ?? ""), [note.data?.bodyText]);
  const media = useMemo(() => inlineMedia(id, attachments.data ?? []), [id, attachments.data]);
  const images = allImages.filter((a) => !snapshots.includes(a) && !inlineNames.has(baseName(a)));
  const videos = attachments.data?.filter((a) => isVideo(a.mime) && !inlineNames.has(baseName(a))) ?? [];
  const files = attachments.data?.filter((a) => !isInlineImage(a.mime) && !isVideo(a.mime)) ?? [];
  // Shown right away while the save is on its way, so quick edits build on each other.
  const [categories, setCategories] = useState<string[]>([]);
  const [editing, setEditing] = useState(false);
  const [sharing, setSharing] = useState(false);
  const markdownRef = useRef<HTMLDivElement>(null);
  const markdownHtml = useMemo(
    () => (note.data?.format === "markdown" && !note.data.bodyHtml ? renderMarkdown(note.data.bodyText, media) : null),
    [note.data?.format, note.data?.bodyHtml, note.data?.bodyText, media],
  );

  // Rendered task-list checkboxes come out disabled; enable them so ticking one updates the note.
  useEffect(() => {
    markdownRef.current?.querySelectorAll<HTMLInputElement>('input[type="checkbox"]').forEach((box) => {
      box.disabled = false;
      box.setAttribute("aria-label", box.checked ? "Done: mark as not done" : "Mark as done");
    });
  }, [markdownHtml, editing]);

  useEffect(() => {
    setEditing(false);
  }, [note.data?.id]);

  const savedCategories = note.data?.categories.join(" ");
  useEffect(() => {
    setCategories(note.data?.categories ?? []);
  }, [note.data?.id, savedCategories]);

  if (note.isPending) return <div className="list-state">Loading…</div>;
  if (note.isError || !note.data) {
    return (
      <div className="list-state">
        <p>This note isn't available.</p>
        <button className="btn" onClick={onClose}>Back</button>
      </div>
    );
  }

  const n = note.data;
  const set = (input: Parameters<typeof update.mutate>[0]) => update.mutate(input);

  return (
    <article className="note-detail">
      {!editing && (
        <header className="detail-header">
          <button className="btn btn-small back-btn" onClick={onClose} aria-label="Back to list">
            ← Back
          </button>
          {n.status !== "pending" && <CategoryInput
              value={categories}
              onChange={(next) => {
                setCategories(next);
                set({ id: n.id, categories: next });
              }}
            />}
          <div className="detail-actions">
            {n.status === "active" && (
              <button className="btn btn-small" onClick={() => setSharing(true)}>
                Share
              </button>
            )}
            <Menu
              label="Export ▾"
              items={[
                { label: "Text", hint: ".txt", onSelect: () => exportText(n) },
                { label: "Markdown", hint: ".md", onSelect: () => exportMarkdown(n) },
                { label: "PDF", hint: "Save as PDF", onSelect: () => exportPdf(n, attachments.data ?? []) },
              ]}
            />
            {n.status === "trashed" ? (
              <>
                <button className="btn btn-small" onClick={() => set({ id: n.id, status: "active" })}>
                  Restore
                </button>
                <ConfirmButton
                  label="Delete forever"
                  confirmLabel="Delete forever: sure?"
                  busy={deleteForever.isPending}
                  onConfirm={() => deleteForever.mutate(n.id, { onSuccess: onClose })}
                />
              </>
            ) : (
              <Menu
                label={<span aria-label="More actions">⋯</span>}
                items={[
                  ...(n.status === "active"
                    ? [
                        { label: n.pinned ? "Unpin" : "Pin to top", onSelect: () => set({ id: n.id, pinned: !n.pinned }) },
                        { label: "Archive", onSelect: () => set({ id: n.id, status: "archived" }) },
                      ]
                    : n.status === "archived"
                      ? [{ label: "Unarchive", onSelect: () => set({ id: n.id, status: "active" }) }]
                      : []),
                  "divider" as const,
                  {
                    label: "Delete",
                    danger: true,
                    onSelect: () => {
                      set({ id: n.id, status: "trashed" });
                      onClose();
                    },
                  },
                ]}
              />
            )}
          </div>
        </header>
      )}

      {n.status === "pending" && (
        <div className="pending-bar">
          <p>
            <strong>{n.fromAddr}</strong> isn't one of your trusted senders
            {n.categories.length > 0 && (
              <>
                {" "}
                and wants to file this in <strong>{n.categories.map((c) => `#${c}`).join(" ")}</strong>
              </>
            )}
            .
          </p>
          <div className="pending-actions">
            <button className="btn btn-primary btn-small" onClick={() => approve.mutate({ id: n.id, allowSender: true })}>
              Approve & trust sender
            </button>
            <button className="btn btn-small" onClick={() => approve.mutate({ id: n.id, allowSender: false })}>
              Approve once
            </button>
            <button
              className="btn btn-small btn-danger"
              onClick={() => {
                block.mutate(n.id);
                onClose();
              }}
            >
              Block sender
            </button>
          </div>
        </div>
      )}

      {editing ? (
        <NoteEditor
          initial={{ title: n.title, body: n.bodyText, format: n.format }}
          saving={update.isPending}
          error={update.isError ? "Couldn't save your changes." : null}
          onCancel={() => setEditing(false)}
          onSave={(draft) =>
            update.mutate(
              { id: n.id, title: draft.title, bodyText: draft.body, format: draft.format },
              { onSuccess: () => setEditing(false) },
            )
          }
        />
      ) : (
        <>
          <div className="note-head">
            <h1>{n.title || "Untitled"}</h1>
            <p className="muted small">
              {n.fromAddr ? `From ${n.fromAddr}` : sourceLabel(n.source)} ·{" "}
              {new Date(n.createdAt).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
              {n.sourceUrl && (
                <>
                  {" "}
                  ·{" "}
                  <a href={n.sourceUrl} target="_blank" rel="noreferrer">
                    Open original
                  </a>
                </>
              )}
              {!n.bodyHtml && (
                <>
                  {" "}
                  ·{" "}
                  <button className="link-btn" onClick={() => setEditing(true)}>
                    Edit
                  </button>
                </>
              )}
            </p>
          </div>

          {files.length > 0 && (
            <ul className="attachments">
              {files.map((a) => (
                <li key={a.id}>
                  <a href={a.url ?? attachmentUrl(n.id, a.id)} className="attachment" target="_blank" rel="noreferrer">
                    📎 {a.filename ?? "attachment"} <span className="muted small">{formatSize(a.size)}</span>
                  </a>
                </li>
              ))}
            </ul>
          )}

          {snapshots.map((a) => (
            <Snapshot key={a.id} src={a.url ?? attachmentUrl(n.id, a.id, true)} name={a.filename} />
          ))}

          {n.bodyHtml ? (
            <EmailFrame html={n.bodyHtml} />
          ) : markdownHtml !== null ? (
            <div
              ref={markdownRef}
              className="note-body markdown-body"
              dangerouslySetInnerHTML={{ __html: markdownHtml }}
              onClick={(e) => {
                const box = e.target as HTMLElement;
                if (!(box instanceof HTMLInputElement) || box.type !== "checkbox") return;
                const boxes = [...(markdownRef.current?.querySelectorAll('input[type="checkbox"]') ?? [])];
                update.mutate({ id: n.id, bodyText: toggleTask(n.bodyText, boxes.indexOf(box)) });
              }}
            />
          ) : (
            <div className="note-body">{n.bodyText}</div>
          )}

          {images.length > 0 && (
            <div className={`gallery gallery-${Math.min(images.length, 4)}`}>
              {images.map((a) => (
                <a key={a.id} href={a.url ?? attachmentUrl(n.id, a.id, true)} target="_blank" rel="noreferrer" className="gallery-item">
                  <img src={a.url ?? attachmentUrl(n.id, a.id, true)} alt={a.filename ?? "Image"} loading="lazy" />
                </a>
              ))}
            </div>
          )}

          {videos.map((a) => (
            <figure key={a.id} className="video">
              <video controls preload="metadata" playsInline src={mediaUrl(n.id, a)} />
              <figcaption>
                🎬 {a.filename ?? "video"} <span className="muted small">{formatSize(a.size)}</span>
                <a className="link-btn" href={downloadUrl(n.id, a)}>
                  Download
                </a>
              </figcaption>
            </figure>
          ))}
        </>
      )}
      {sharing && <ShareDialog note={n} onClose={() => setSharing(false)} />}
    </article>
  );
}

/** A page snapshot: tall, so it's shown folded with "Show all" and a link to the full-size image. */
function Snapshot({ src, name }: { src: string; name: string | null }) {
  const [open, setOpen] = useState(false);
  const [tall, setTall] = useState(false);
  const label = (name ?? "Snapshot").replace(/\.(jpe?g|png|webp)$/i, "").replace(/^Snapshot - /, "Snapshot · ");
  return (
    <>
      <p className="snapshot-label">📸 {label}</p>
      <figure className={`snapshot${tall && !open ? " collapsed" : ""}`}>
        <img
          src={src}
          alt={label}
          onLoad={(e) => {
            const img = e.currentTarget;
            setTall(img.naturalHeight / img.naturalWidth > 1.2);
          }}
        />
        {tall && (
          <div className="snapshot-bar">
            <button className="btn" onClick={() => setOpen(!open)}>
              {open ? "Fold" : "Show all"}
            </button>
            <a className="btn" href={src} target="_blank" rel="noreferrer">
              Open image
            </a>
          </div>
        )}
      </figure>
    </>
  );
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
